/* eslint-disable */
import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Button,
  Flex,
  Text,
  Badge,
  Grid,
  GridItem,
  IconButton,
  useToast,
  useColorModeValue,
} from "@chakra-ui/react";

import { DownloadIcon, ChevronLeftIcon, ChevronRightIcon } from "@chakra-ui/icons";
import { AgGridProvider, AgGridReact } from "ag-grid-react";
import { AllCommunityModule } from "ag-grid-community";

// Import Chart.js & Komponen BAR CHART 
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip as ChartTooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";

import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

import axiosClient from "../../../api/axiosClient";

// Registrasi BarElement untuk grafik batang
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  ChartTooltip,
  Legend
);

const modules = [AllCommunityModule];

export default function DynamicTable({
  endpoint,
  title = "Laporan Waktu Penyelesaian Tiket",
}) {
  const toast = useToast();
  
  // Theme Color System
  const cardBg = useColorModeValue("white", "navy.700");
  const subCardBg = useColorModeValue("gray.50", "navy.800");
  const textColor = useColorModeValue("secondaryGray.900", "white");
  const borderColor = useColorModeValue("#edf2f7", "navy.600");

  const [data, setData] = useState([]);
  const [meta, setMeta] = useState({});
  const [page, setPage] = useState(1);

  // --- Parser Durasi & Status SLA Bawaan Anda ---
  const formatDateTime = (value) => {
    if (!value) return "-";
    return new Date(value).toLocaleString("id-ID", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  };

  const diffDuration = (start, end) => {
    if (!start || !end) return "-";
    const startDate = new Date(start);
    const endDate = new Date(end);
    const diffMs = endDate - startDate;
    if (diffMs < 0) return "-";

    const totalMinutes = Math.floor(diffMs / 1000 / 60);
    const days = Math.floor(totalMinutes / 1440);
    const hours = Math.floor((totalMinutes % 1440) / 60);
    const minutes = totalMinutes % 60;

    if (days > 0) return `${days} hari ${hours} jam ${minutes} menit`;
    if (hours > 0) return `${hours} jam ${minutes} menit`;
    return `${minutes} menit`;
  };

  const findCommentDateByStatus = (row, keyword) => {
    const comments = row.comments || [];
    const found = comments.find((comment) =>
      comment.message?.toLowerCase().includes(keyword)
    );
    return found?.created_at || null;
  };

  const getMulaiDiproses = (row) => {
    return findCommentDateByStatus(row, "diproses") || findCommentDateByStatus(row, "proses") || null;
  };

  const getSelesai = (row) => {
    if (row.status === "selesai") {
      return findCommentDateByStatus(row, "selesai") || row.updated_at || null;
    }
    return null;
  };

  const getSlaLimitHours = (urgensi) => {
    switch ((urgensi || "").toLowerCase()) {
      case "tinggi": return 4;
      case "sedang": return 24;
      case "rendah": return 72;
      default: return 72;
    }
  };

  const getSlaStatus = (row) => {
    const createdAt = row.created_at;
    const selesaiAt = getSelesai(row);
    if (!createdAt || !selesaiAt) return "Belum Selesai";

    const diffHours = (new Date(selesaiAt) - new Date(createdAt)) / 1000 / 60 / 60;
    const limit = getSlaLimitHours(row.urgensi);
    return diffHours <= limit ? "Sesuai SLA" : "Melewati SLA";
  };

  const mapRows = () => {
    return data.map((row, index) => {
      const dibuat = row.created_at;
      const mulaiDiproses = getMulaiDiproses(row);
      const selesai = getSelesai(row);

      return {
        no: (page - 1) * (meta?.per_page ?? data.length) + index + 1,
        no_tiket: row.number_ticket || `#${row.id}`,
        prioritas: row.urgensi || "-",
        dibuat: formatDateTime(dibuat),
        mulai_diproses: formatDateTime(mulaiDiproses),
        selesai: formatDateTime(selesai),
        durasi_respon: diffDuration(dibuat, mulaiDiproses),
        durasi_penyelesaian: diffDuration(dibuat, selesai),
        status_sla: getSlaStatus(row),
      };
    });
  };

  const loadData = async () => {
    try {
      const res = await axiosClient.get(`${endpoint}?page=${page}`);
      setData(res.data.data || []);
      setMeta(res.data.meta || {});
    } catch (error) {
      toast({
        title: "Gagal memuat laporan",
        description: error.response?.data?.message || "Terjadi kesalahan",
        status: "error",
        duration: 3000,
        isClosable: true,
      });
    }
  };

  useEffect(() => {
    loadData();
  }, [page, endpoint]);

  const rows = useMemo(() => mapRows(), [data, page, meta]);

  // --- 📈 LOGIC UTK METRIC CARD & BAR CHART PERBANDINGAN SLA 📈 ---
  const statsSummary = useMemo(() => {
    let sesuai = 0;
    let melewati = 0;
    let belum = 0;

    rows.forEach(r => {
      if (r.status_sla === "Sesuai SLA") sesuai++;
      else if (r.status_sla === "Melewati SLA") melewati++;
      else belum++;
    });

    const totalResolved = sesuai + melewati;
    const rateSla = totalResolved > 0 ? Math.round((sesuai / totalResolved) * 100) : 0;

    return { sesuai, melewati, belum, rateSla };
  }, [rows]);

  const chartConfig = useMemo(() => {
    // Kelompokkan data berdasarkan tanggal dan status SLA-nya
    const dailyGroups = {};

    data.forEach(item => {
      if (item.created_at) {
        const dateLabel = new Date(item.created_at).toLocaleDateString("id-ID", {
          day: "numeric",
          month: "short"
        });

        if (!dailyGroups[dateLabel]) {
          dailyGroups[dateLabel] = { sesuai: 0, melewati: 0 };
        }

        const currentStatus = getSlaStatus(item);
        if (currentStatus === "Sesuai SLA") {
          dailyGroups[dateLabel].sesuai += 1;
        } else if (currentStatus === "Melewati SLA") {
          dailyGroups[dateLabel].melewati += 1;
        }
      }
    });

    const labels = Object.keys(dailyGroups);
    const dataSesuai = labels.map(lbl => dailyGroups[lbl].sesuai);
    const dataMelewati = labels.map(lbl => dailyGroups[lbl].melewati);

    return {
      data: {
        labels: labels,
        datasets: [
          {
            label: "Sesuai SLA",
            data: dataSesuai,
            backgroundColor: "#48BB78", // Hijau (Success)
            borderRadius: 6,
          },
          {
            label: "Melewati SLA",
            data: dataMelewati,
            backgroundColor: "#E53E3E", // Merah (Danger)
            borderRadius: 6,
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            display: true, // Tampilkan legenda label di atas grafik
            position: "top",
            labels: { boxWidth: 12, font: { size: 11, weight: "600" }, color: "#A3AED0" }
          }
        },
        scales: {
          x: { grid: { display: false }, ticks: { color: "#A3AED0", font: { size: 10 } } },
          y: { beginAtZero: true, grid: { color: borderColor }, ticks: { color: "#A3AED0", stepSize: 1 } }
        }
      }
    };
  }, [data, borderColor]);

  // --- Handler Export ---
  const handleExportExcel = () => {
    const worksheet = XLSX.utils.json_to_sheet(rows);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, "Waktu Penyelesaian");
    XLSX.writeFile(workbook, "laporan-waktu-penyelesaian-tiket.xlsx");
  };

  const handleExportPdf = () => { /* ... kode export PDF Anda ... */ };
  const handlePrint = () => { /* ... kode print Anda ... */ };

  // --- Cell Renderer AG Grid ---
  const PriorityBadge = (params) => {
    const value = params.value || "-";
    const color = value === "tinggi" ? "red" : value === "sedang" ? "orange" : "green";
    return <Badge colorScheme={color} variant="solid" borderRadius="6px" px="8px">{value.toUpperCase()}</Badge>;
  };

  const StatusSlaBadge = (params) => {
    const value = params.value;
    const color = value === "Sesuai SLA" ? "green" : value === "Melewati SLA" ? "red" : "gray";
    return <Badge colorScheme={color} variant="subtle" px="10px" py="4px" borderRadius="8px">{value}</Badge>;
  };

  const columnDefs = useMemo(() => [
    { headerName: "No", field: "no", width: 75, pinned: "left", filter: false, sortable: false },
    { headerName: "No Tiket", field: "no_tiket", minWidth: 130, flex: 1, pinned: "left" },
    { headerName: "Prioritas", field: "prioritas", minWidth: 110, flex: 1, cellRenderer: PriorityBadge },
    { headerName: "Dibuat", field: "dibuat", minWidth: 160, flex: 1 },
    { headerName: "Mulai Diproses", field: "mulai_diproses", minWidth: 160, flex: 1 },
    { headerName: "Selesai", field: "selesai", minWidth: 160, flex: 1 },
    { headerName: "Durasi Respon", field: "durasi_respon", minWidth: 150, flex: 1 },
    { headerName: "Durasi Penyelesaian", field: "durasi_penyelesaian", minWidth: 180, flex: 1 },
    { headerName: "Status SLA", field: "status_sla", minWidth: 140, flex: 1, cellRenderer: StatusSlaBadge },
  ], []);

  const defaultColDef = useMemo(() => ({ sortable: true, filter: true, resizable: true, floatingFilter: true }), []);

  return (
    <Box bg={cardBg} borderRadius="24px" boxShadow="0 18px 40px rgba(112, 144, 176, 0.12)" p="24px" w="100%">
      
      {/* HEADER SECTION */}
      <Flex justify="space-between" align={{ base: "start", md: "center" }} direction={{ base: "column", md: "row" }} gap="14px" mb="24px">
        <Box>
          <Text fontSize="22px" fontWeight="800" color={textColor} letterSpacing="-0.5px">
            {title}
          </Text>
          <Text fontSize="sm" color="gray.500">
            Modul Analisis Eksekutif & Monitoring SLA Target Gangguan IT Helpdesk
          </Text>
        </Box>

        <Flex gap="10px" flexWrap="wrap">
          <Button variant="outline" colorScheme="gray" size="sm" borderRadius="10px" onClick={handlePrint}>Print</Button>
          <Button colorScheme="red" size="sm" borderRadius="10px" onClick={handleExportPdf}>PDF</Button>
          <Button colorScheme="green" leftIcon={<DownloadIcon />} size="sm" borderRadius="10px" onClick={handleExportExcel}>Excel</Button>
        </Flex>
      </Flex>

      {/* METRIC SUMMARY CARDS */}
      <Grid templateColumns={{ base: "1fr", md: "repeat(3, 1fr)" }} gap="16px" mb="24px">
        <GridItem bg={subCardBg} p="20px" borderRadius="16px" border={`1px solid ${borderColor}`}>
          <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase">SLA Achievement Rate</Text>
          <Text fontSize="26px" fontWeight="800" color={statsSummary.rateSla >= 75 ? "green.500" : "orange.500"} mt="4px">
            {statsSummary.rateSla}%
          </Text>
          <Text fontSize="xs" color="gray.500" mt="2px">Rasio ketepatan pemenuhan SLA</Text>
        </GridItem>

        <GridItem bg={subCardBg} p="20px" borderRadius="16px" border={`1px solid ${borderColor}`}>
          <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase">Tepat SLA</Text>
          <Text fontSize="26px" fontWeight="800" color="green.500" mt="4px">{statsSummary.sesuai} Kasus</Text>
          <Text fontSize="xs" color="gray.500" mt="2px">Tiket selesai sesuai koridor waktu</Text>
        </GridItem>

        <GridItem bg={subCardBg} p="20px" borderRadius="16px" border={`1px solid ${borderColor}`}>
          <Text fontSize="xs" fontWeight="700" color="gray.400" textTransform="uppercase">Melewati Batas SLA</Text>
          <Text fontSize="26px" fontWeight="800" color="red.500" mt="4px">{statsSummary.melewati} Kasus</Text>
          <Text fontSize="xs" color="red.400" fontWeight="600" mt="2px">⚠ Memerlukan evaluasi operasional</Text>
        </GridItem>

        {/* 📊 BAR CHART PERBANDINGAN AKURASI SLA 📊 */}
        <GridItem colSpan={{ base: 1, md: 3 }} bg={subCardBg} p="20px" borderRadius="16px" border={`1px solid ${borderColor}`}>
          <Text fontSize="sm" fontWeight="800" color={textColor} mb="12px">
            Analisis Komparatif Pemenuhan Target SLA Harian
          </Text>
          <Flex h="200px" w="100%" position="relative">
            <Bar data={chartConfig.data} options={chartConfig.options} />
          </Flex>
        </GridItem>
      </Grid>

      {/* TABLE DATA CONTAINER (AG GRID) */}
      <Box className="ag-theme-quartz" w="100%" h="380px" borderRadius="18px" overflow="hidden"
        sx={{
          ".ag-root-wrapper": { borderRadius: "18px", border: `1px solid ${borderColor}` },
          ".ag-header": { background: useColorModeValue("#f8fafc", "navy.800"), fontWeight: "800" },
          ".ag-row": { fontSize: "14px" },
        }}
      >
        <AgGridProvider modules={modules}>
          <AgGridReact
            rowData={rows}
            columnDefs={columnDefs}
            defaultColDef={defaultColDef}
            rowHeight={52}
            headerHeight={48}
            animateRows={true}
            suppressCellFocus={true}
            pagination={false}
            domLayout="normal"
          />
        </AgGridProvider>
      </Box>

      {/* PAGINATION SYSTEM */}
      <Flex mt="20px" justifyContent="space-between" alignItems="center" px="4px">
        <Text fontSize="sm" color="gray.500" fontWeight="500">
          Menampilkan {data.length} dari total kolektif riwayat
        </Text>
        <Flex alignItems="center" gap="12px">
          <IconButton aria-label="Prev Page" icon={<ChevronLeftIcon w={5} h={5} />} onClick={() => setPage((p) => Math.max(1, p - 1))} isDisabled={page === 1} size="sm" variant="outline" borderRadius="8px" />
          <Text color={textColor} fontSize="sm" fontWeight="700">Halaman {page} dari {meta?.total_pages || 1}</Text>
          <IconButton aria-label="Next Page" icon={<ChevronRightIcon w={5} h={5} />} onClick={() => setPage((p) => p + 1)} isDisabled={page >= (meta?.total_pages || 1)} size="sm" variant="outline" borderRadius="8px" />
        </Flex>
      </Flex>
    </Box>
  );
}